import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { Button, Checkbox, Empty, Input, Spin, Tooltip, message, theme } from 'antd';
import { CloseOutlined, DownOutlined, SearchOutlined, UpOutlined } from '@ant-design/icons';
import { Trans, useTranslation } from 'react-i18next';

import { NoPermission } from '../../../components/NoPermission';
import { ResourceCode } from '../../../constants/resourceCodes';
import { useWarehouseContext } from '../../../contexts/WarehouseContext';
import {
    IS_MANUAL_PUSH_WAREHOUSE,
    IsManualPushWarehouseResponse,
    IsManualPushWarehouseVariables,
} from '../../../graphql/queries/isManualPushWarehouse';
import { hasResource } from '../../../utils/resourceAuth';
import { translateGraphQLError } from '../../../utils/graphqlUtils';
import {
    BatchPushCellInput,
    BatchPushPreview,
    GET_BATCH_PUSH_BOARD,
    GetBatchPushBoardResponse,
    GetBatchPushBoardVariables,
    PREVIEW_BATCH_PUSH,
    PreviewBatchPushResponse,
    PreviewBatchPushVariables,
    SUBMIT_BATCH_PUSH,
    SubmitBatchPushResponse,
    SubmitBatchPushVariables,
} from './graphql/batchPush';
import {
    areaGroupKey,
    buildCellCountMap,
    formatColumnTime,
    formatNumber,
    matchAreas,
    newRequestNo,
    pruneSelection,
    summarizeSelection,
    toCellInputs,
    toggleAllKeys,
    toggleKeys,
    triState,
} from './batchPushUtils';
import BatchPushMatrix from './components/BatchPushMatrix';
import BatchPushEntryHint from './components/BatchPushEntryHint';
import BatchPushConfirmModal from './components/BatchPushConfirmModal';
import BatchPushResultPanel from './components/BatchPushResultPanel';
import PushBatchTable from './components/PushBatchTable';
import { usePushingWatcher } from './usePushingWatcher';

const FALLBACK_ERROR_KEY = 'wms.batchPush.messages.operationFailed';

/**
 * 分批次-尾程「批次推送」页（手动推送模式仓的仓管使用）。
 * 选择页：批次 × 区域矩阵勾选 → 查看已选明细 → 确认推送（一次前置比对）→ 提交；
 * 结果页：轮询推送中 / 成功 / 失败，失败可重新推送（原单元格 + 新幂等号）或返回修改（保留勾选）。
 */
const BatchPushPage: React.FC = () => {
    const { t } = useTranslation();
    const { token } = theme.useToken();
    const navigate = useNavigate();
    const { currentWarehouse } = useWarehouseContext();
    const permitted = hasResource([ResourceCode.menu.WHS_DELIVERY_BATCH_PUSH]);
    const warehouseId = Number(currentWarehouse?.id ?? 0);

    const [selected, setSelected] = useState<Set<string>>(new Set());
    const [folded, setFolded] = useState<Set<string>>(new Set());
    const [search, setSearch] = useState('');
    const [detailOpen, setDetailOpen] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [preview, setPreview] = useState<BatchPushPreview | null>(null);
    const [pushRecordId, setPushRecordId] = useState<number | null>(null);
    const [lastCells, setLastCells] = useState<BatchPushCellInput[]>([]);

    const { data: manualData, loading: manualLoading } = useQuery<IsManualPushWarehouseResponse, IsManualPushWarehouseVariables>(
        IS_MANUAL_PUSH_WAREHOUSE,
        { variables: { warehouseId }, skip: !permitted || !warehouseId, fetchPolicy: 'network-only' },
    );
    const manual = manualData?.manual === true;

    const { data: boardData, loading: boardLoading, refetch: refetchBoard } = useQuery<GetBatchPushBoardResponse, GetBatchPushBoardVariables>(
        GET_BATCH_PUSH_BOARD,
        {
            variables: { warehouseId },
            skip: !manual,
            fetchPolicy: 'network-only',
            onError: (e) => message.error(translateGraphQLError(e, FALLBACK_ERROR_KEY)),
        },
    );
    const board = boardData?.board;
    const counts = useMemo(() => buildCellCountMap(board), [board]);
    // 本仓进行中的推送：「确认推送」loading，旁边展示推送人与发起时间；结束后刷新看板（不额外提示成功 / 失败）
    const pushing = board?.pushing ?? null;
    usePushingWatcher(warehouseId, pushing, manual && pushRecordId == null, () => refetchBoard());
    const pushingTime = useMemo(() => formatColumnTime(pushing?.startedAt) ?? '', [pushing]);
    const summary = useMemo(() => summarizeSelection(board, selected, counts), [board, selected, counts]);
    const hitAreaIds = useMemo(() => matchAreas(board?.areas ?? [], search), [board, search]);

    const [loadPreview, { loading: previewLoading }] = useLazyQuery<PreviewBatchPushResponse, PreviewBatchPushVariables>(
        PREVIEW_BATCH_PUSH,
        { fetchPolicy: 'network-only' },
    );
    const [submit, { loading: submitting }] = useMutation<SubmitBatchPushResponse, SubmitBatchPushVariables>(SUBMIT_BATCH_PUSH);

    // 切仓：清空页面状态
    useEffect(() => {
        setSelected(new Set());
        setFolded(new Set());
        setSearch('');
        setDetailOpen(false);
        setConfirmOpen(false);
        setPushRecordId(null);
    }, [warehouseId]);

    // 看板刷新后剔除已不存在的单元格（已被推送、出池），保留仍可推的勾选
    useEffect(() => {
        setSelected((prev) => pruneSelection(prev, counts));
    }, [counts]);

    const toggle = useCallback((keys: string[]) => setSelected((prev) => toggleKeys(keys, prev)), []);
    const toggleAll = useCallback((keys: string[]) => setSelected((prev) => toggleAllKeys(keys, prev)), []);

    // 搜索命中的区域如果在已收起的分组里，自动展开该分组，保证高亮可见、可滚动到
    useEffect(() => {
        if (hitAreaIds.length === 0) return;
        const hitIds = new Set(hitAreaIds);
        const hitGroups = new Set(
            (board?.areas ?? []).filter((a) => a.areaId != null && hitIds.has(Number(a.areaId))).map((a) => areaGroupKey(a.areaName)),
        );
        setFolded((prev) => {
            if (!Array.from(prev).some((g) => hitGroups.has(g))) return prev;
            return new Set(Array.from(prev).filter((g) => !hitGroups.has(g)));
        });
    }, [hitAreaIds, board]);
    const toggleFold = useCallback((group: string) => {
        setFolded((prev) => {
            const next = new Set(prev);
            if (next.has(group)) next.delete(group); else next.add(group);
            return next;
        });
    }, []);

    /** 点「确认推送」：调用一次前置比对，打开确认弹框 */
    const openConfirm = async () => {
        const cells = toCellInputs(selected, counts);
        if (cells.length === 0 || pushing) return;
        setPreview(null);
        setConfirmOpen(true);
        try {
            const res = await loadPreview({ variables: { request: { warehouseId, cells } } });
            if (res.error) throw res.error;
            setPreview(res.data?.preview ?? null);
        } catch (e) {
            setConfirmOpen(false);
            message.error(translateGraphQLError(e, FALLBACK_ERROR_KEY));
        }
    };

    /** 提交推送（确认弹框、失败重推共用）；每次提交生成新幂等号 */
    const doSubmit = async (cells: BatchPushCellInput[]) => {
        try {
            const res = await submit({ variables: { request: { warehouseId, requestNo: newRequestNo(), cells } } });
            const id = res.data?.pushRecordId;
            if (id == null) {
                message.error(t(FALLBACK_ERROR_KEY));
                return;
            }
            setLastCells(cells);
            setConfirmOpen(false);
            setDetailOpen(false);
            setPushRecordId(Number(id));
        } catch (e) {
            message.error(translateGraphQLError(e, FALLBACK_ERROR_KEY));
        }
    };

    const backToSelect = () => {
        setPushRecordId(null);
        refetchBoard();
    };

    const continuePush = () => {
        setSelected(new Set());
        backToSelect();
    };

    if (!permitted) return <NoPermission />;

    const header = (
        <div className="flex items-center mb-4">
            <div className="border-l-4 border-indigo-600 pl-2 text-xl font-medium">{t('wms.batchPush.title')}</div>
        </div>
    );

    if (manualLoading) {
        return <div className="p-6">{header}<Spin /></div>;
    }
    if (!manual) {
        return (
            <div className="p-6">
                {header}
                <div className="bg-white rounded-lg shadow-sm py-16">
                    <Empty description={t('wms.batchPush.notManual')} />
                </div>
            </div>
        );
    }

    if (pushRecordId != null) {
        return (
            <div className="p-6">
                {header}
                <div className="bg-white rounded-lg shadow-sm">
                    <BatchPushResultPanel
                        warehouseId={warehouseId}
                        pushRecordId={pushRecordId}
                        retrying={submitting}
                        onViewHistory={() => navigate('/wms/batch-push-history')}
                        onContinue={continuePush}
                        onRetry={() => doSubmit(lastCells)}
                        onBack={backToSelect}
                    />
                </div>
            </div>
        );
    }

    const hasSelection = summary.total > 0;
    const allCellKeys = Array.from(counts.keys());
    const allState = triState(allCellKeys, selected);
    const searchMiss = search.trim() !== '' && hitAreaIds.length === 0;

    return (
        <div className="batch-push-page p-6 pb-0 flex flex-col" style={{ minHeight: '100%' }}>
            {/* 半选（部分选中）复选框按设计稿：主题色实心底 + 白色横线（antd 默认为白底 + 小方块），只作用于本页 */}
            <style>{`
                .batch-push-page .ant-checkbox-indeterminate .ant-checkbox-inner,
                .batch-push-page .ant-checkbox-wrapper:hover .ant-checkbox-indeterminate .ant-checkbox-inner,
                .batch-push-page .ant-checkbox-indeterminate:hover .ant-checkbox-inner {
                    background-color: ${token.colorPrimary} !important;
                    border-color: ${token.colorPrimary} !important;
                }
                .batch-push-page .ant-checkbox-indeterminate .ant-checkbox-inner::after {
                    width: 8px !important;
                    height: 2px !important;
                    border-radius: 1px;
                    background-color: #fff !important;
                }
            `}</style>
            {header}
            {/* 分批次-尾程：按准入节点说明待推送池口径，「历史包裹追踪」跳转同线路页 */}
            {board && <BatchPushEntryHint warehouseId={warehouseId} entryNode={board.entryNode} timezone={board.timezone} />}
            <Spin spinning={boardLoading}>
                <div className="bg-white rounded-lg shadow-sm">
                    <div className="px-4 py-3 border-b text-base">
                        {t('wms.batchPush.pendingLabel')} <b className="text-lg">{formatNumber(board?.pendingTotal)}</b> {t('wms.batchPush.unit')}
                        <span className="text-gray-500 mx-2">=</span>
                        {t('wms.batchPush.returnRedispatch')} <b>{formatNumber(board?.returnRedispatchCount)}</b>
                        <span className="text-gray-500 mx-2">+</span>
                        {t('wms.batchPush.newArrival')} <b>{formatNumber(board?.newArrivalCount)}</b>
                    </div>
                    {/* 选择推送范围工具栏：上下内边距 16px，加大可点击区域 */}
                    <div className="px-4 py-4 flex items-center gap-3">
                        <span className="font-medium">{t('wms.batchPush.selectScope')}</span>
                        {/* PRD「全选操作：选中所有格子」：工具栏全选，与矩阵左上角全选框同一规则 */}
                        <Checkbox
                            className="border rounded-full px-3 py-0.5"
                            checked={allState === 2}
                            indeterminate={allState === 1}
                            disabled={allCellKeys.length === 0}
                            onChange={() => toggleAll(allCellKeys)}
                            data-testid="toolbar-select-all"
                        >
                            {t('wms.batchPush.selectAll')}
                        </Checkbox>
                        <Input
                            allowClear
                            prefix={<SearchOutlined className="text-gray-400" />}
                            style={{ width: 320 }}
                            placeholder={String(t('wms.batchPush.searchPlaceholder'))}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                        {searchMiss && <span className="text-orange-500 text-sm">{t('wms.batchPush.searchMiss')}</span>}
                    </div>
                    <div className="px-4 pb-4">
                        {board && (board.areas?.length ?? 0) > 0 ? (
                            <BatchPushMatrix
                                board={board}
                                counts={counts}
                                selected={selected}
                                onToggle={toggle}
                                onToggleAll={toggleAll}
                                folded={folded}
                                onToggleFold={toggleFold}
                                hitAreaIds={hitAreaIds}
                            />
                        ) : (
                            !boardLoading && <Empty description={t('wms.batchPush.empty')} />
                        )}
                    </div>
                </div>
            </Spin>

            {/* 底部悬停栏：区域收起时也固定在底部，不上移（PRD）；样式对齐原型（浅紫底、顶部紫色描边、上方阴影） */}
            <div
                className="sticky bottom-0 z-20 mt-auto -mx-6 bg-indigo-50 border-t-2 border-indigo-200"
                style={{ boxShadow: '0 -6px 16px rgba(91, 33, 182, 0.08)' }}
            >
                {/* 已选明细：从底部栏向上浮出的面板（原型 .detail），不挤占底部栏 */}
                {detailOpen && hasSelection && (
                    <div
                        className="absolute left-0 right-0 bottom-full bg-white border border-b-0 border-indigo-200 rounded-t-xl overflow-auto"
                        style={{ boxShadow: '0 -10px 30px rgba(0, 0, 0, 0.12)', maxHeight: 'calc(100vh - 110px)', padding: '14px 20px' }}
                        data-testid="batch-push-detail"
                    >
                        <div className="flex items-center mb-2">
                            <span className="text-sm font-semibold">{t('wms.batchPush.detail.title')}</span>
                            <span className="flex-1" />
                            <Button type="link" size="small" style={{ padding: 0 }} onClick={() => setDetailOpen(false)}>
                                {t('wms.batchPush.detail.close')} <CloseOutlined />
                            </Button>
                        </div>
                        <div className="rounded-lg border border-amber-200 bg-amber-50 mb-3" style={{ padding: '12px 16px', fontSize: 13, lineHeight: 1.8 }}>
                            <Trans
                                i18nKey="wms.batchPush.summaryBanner"
                                values={{
                                    total: formatNumber(summary.total),
                                    returnRedispatch: formatNumber(summary.returnRedispatch),
                                    newArrival: formatNumber(summary.newArrival),
                                }}
                                components={{ b: <b /> }}
                            />
                        </div>
                        <PushBatchTable
                            rows={summary.batches.map((b) => ({
                                key: b.column.wmsBatchNumber ?? '',
                                column: b.column,
                                count: b.count,
                                areaNames: b.areaNames,
                            }))}
                        />
                    </div>
                )}
                <div className="flex items-center gap-4 px-6 py-3">
                    <span style={{ fontSize: 15 }}>
                        {t('wms.batchPush.footer.selected')}{' '}
                        <b className="text-indigo-600 tabular-nums" style={{ fontSize: 24 }}>{formatNumber(summary.total)}</b>
                        <span className="text-gray-500 tabular-nums" style={{ fontSize: 13 }}> / {t('wms.batchPush.footer.pending')} {formatNumber(board?.pendingTotal)}</span>
                    </span>
                    <Tooltip title={hasSelection ? '' : t('wms.batchPush.footer.selectFirst')}>
                        <Button
                            color="primary"
                            variant="outlined"
                            className="font-semibold"
                            disabled={!hasSelection}
                            onClick={() => setDetailOpen(!detailOpen)}
                        >
                            {detailOpen ? t('wms.batchPush.footer.hideDetail') : t('wms.batchPush.footer.viewDetail')}
                            {detailOpen ? <DownOutlined /> : <UpOutlined />}
                        </Button>
                    </Tooltip>
                    <span className="flex-1" />
                    {pushing && (
                        <span className="text-orange-500" data-testid="batch-push-pushing">
                            {t('wms.batchPush.footer.pushingBy', { operator: pushing.operatorName || '-', time: pushingTime })}
                        </span>
                    )}
                    <Tooltip title={hasSelection || pushing ? '' : t('wms.batchPush.footer.selectFirst')}>
                        <Button
                            type="primary"
                            size="large"
                            className="font-semibold"
                            loading={!!pushing}
                            disabled={!hasSelection && !pushing}
                            onClick={openConfirm}
                        >
                            {t('wms.batchPush.footer.confirmPush', { num: formatNumber(summary.total) })}
                        </Button>
                    </Tooltip>
                </div>
            </div>

            <BatchPushConfirmModal
                open={confirmOpen}
                loading={previewLoading}
                preview={preview}
                areas={board?.areas ?? []}
                submitting={submitting}
                onBack={() => setConfirmOpen(false)}
                onConfirm={() => doSubmit(toCellInputs(selected, counts))}
            />
        </div>
    );
};

export default BatchPushPage;
